import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthTokenService } from '../services/auth-token.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { silentClearSession } from '../state/auth/auth.actions';
import { NotificationService } from '../services/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private webAccessHandled = false;

  constructor(
    private authTokenService: AuthTokenService,
    private router: Router,
    private store: Store,
    private notification: NotificationService,
    private translate: TranslateService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authTokenService.getToken();

    let authReq = req;
    if (token) {
      authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        const code = error?.error?.code;
        const isAuthLoginRequest = req.url.includes('/auth/login') || req.url.includes('/auth/google');

        if (error.status === 403 && code === 'WEB_PORTAL_ACCESS_DENIED' && !isAuthLoginRequest) {
          this.authTokenService.clearToken();
          this.store.dispatch(silentClearSession());

          if (!this.webAccessHandled) {
            this.webAccessHandled = true;
            this.notification.info(this.translate.instant('auth.notifications.webPortalAccessDenied'));
          }

          if (this.router.url !== '/download') {
            this.router.navigate(['/download']);
          }

          return throwError(() => error);
        }

        if (error.status !== 401 || req.url.includes('/refresh-token') || req.url.includes('/logout')) {
          return throwError(() => error);
        }

        return this.authTokenService.refresh().pipe(
          switchMap(({ accessToken }) => {
            const newReq = req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } });
            return next.handle(newReq);
          }),
          catchError(err => {
            this.authTokenService.clearToken();
            this.router.navigate(['/login']);
            return throwError(() => err);
          })
        );
      })
    );
  }
}
